import { RadioClient } from '../transport/radioClient';
import { FusionEngine } from '../storage/fusionEngine';
import { showToast } from './toast';

/**
 * Configuration options required to instantiate ConnectionModalController.
 *
 * Why: Provides decoupled access to RadioClient and FusionEngine instances without hardcoupling to Dashboard.
 */
export interface ConnectionModalControllerOptions {
  radioClient: RadioClient;
  fusionEngine: FusionEngine;
  getTransportName: () => string;
}

/**
 * Controller managing the tactical hardware connection modal dialog.
 *
 * Why: Decouples hardware selection (WebSerial vs. WebBluetooth), live NodeDB sync triggers,
 * session disconnect, and autoreconnect cancellation from the main Dashboard orchestrator.
 */
export class ConnectionModalController {
  private radioClient: RadioClient;
  private fusionEngine: FusionEngine;
  private getTransportName: () => string;

  constructor(options: ConnectionModalControllerOptions) {
    this.radioClient = options.radioClient;
    this.fusionEngine = options.fusionEngine;
    this.getTransportName = options.getTransportName;
  }

  /**
   * Initializes event listeners for the connection management modal dialog.
   *
   * Why: Enables choosing transport methods on desktop, performing manual NodeDB
   * synchronizations, terminating radio sessions, and closing the dialog via buttons, backdrop, or Escape.
   */
  init(): void {
    const backdrop = document.getElementById('connection-modal-backdrop');
    const btnClose = document.getElementById('modal-btn-close');
    const btnSerial = document.getElementById('modal-btn-serial');
    const btnBle = document.getElementById('modal-btn-ble');
    const btnActionSync = document.getElementById('modal-action-sync');
    const btnActionDisconnect = document.getElementById('modal-action-disconnect');

    if (btnClose) {
      btnClose.addEventListener('click', () => this.close());
    }

    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          this.close();
        }
      });
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });

    if (btnSerial) {
      btnSerial.addEventListener('click', async () => {
        this.close();
        try {
          await this.radioClient.connect('serial');
          showToast('USB Seriell Verbindung hergestellt', 'success');
        } catch (err) {
          showToast(`Serielle Verbindung fehlgeschlagen: ${(err as Error).message}`, 'error');
        }
      });
    }

    if (btnBle) {
      btnBle.addEventListener('click', async () => {
        this.close();
        try {
          await this.radioClient.connect('ble');
          showToast('Bluetooth LE Verbindung hergestellt', 'success');
        } catch (err) {
          showToast(`Bluetooth-Verbindung fehlgeschlagen: ${(err as Error).message}`, 'error');
        }
      });
    }

    if (btnActionSync) {
      btnActionSync.addEventListener('click', async () => {
        await this.radioClient.requestNodeDbSync();
        this.update();
      });
    }

    if (btnActionDisconnect) {
      btnActionDisconnect.addEventListener('click', async () => {
        await this.radioClient.disconnect();
        this.update();
      });
    }
  }

  /**
   * Opens the tactical connection management modal and syncs its state.
   */
  open(): void {
    const backdrop = document.getElementById('connection-modal-backdrop');
    if (backdrop) {
      this.update();
      backdrop.style.display = 'flex';
    }
  }

  /**
   * Closes the tactical connection management modal.
   */
  close(): void {
    const backdrop = document.getElementById('connection-modal-backdrop');
    if (backdrop) {
      backdrop.style.display = 'none';
    }
  }

  /**
   * Updates the connection modal contents based on the radio client's live state.
   *
   * Why: Dynamically switches the modal between the transport selection cards (when disconnected)
   * and live node diagnostics/disconnect actions (when connected or reconnecting).
   */
  update(): void {
    const state = this.radioClient.getState();
    const indicator = document.getElementById('modal-status-indicator');
    const title = document.getElementById('modal-status-title');
    const detail = document.getElementById('modal-status-detail');
    const connectOptions = document.getElementById('modal-connect-options');
    const connectedActions = document.getElementById('modal-connected-actions');

    const btnSync = document.getElementById('modal-action-sync');
    const disconnectSpan = document.querySelector('#modal-action-disconnect span');

    if (!indicator || !title || !detail || !connectOptions || !connectedActions) return;

    indicator.className = 'status-indicator';

    if (state === 'CONNECTED') {
      indicator.classList.add('connected');
      title.textContent = 'Verbunden';
      const count = this.radioClient.getSyncedNodesCount();
      detail.textContent = `${count} Nodes im Speicher`;

      connectOptions.style.display = 'none';
      connectedActions.style.display = 'flex';
      if (btnSync) btnSync.style.display = '';
      if (disconnectSpan) disconnectSpan.textContent = 'Trennen';

      const myInfo = this.radioClient.getMyNodeInfo();
      const nodeNameEl = document.getElementById('modal-node-name');
      const nodeHexEl = document.getElementById('modal-node-hex');
      const transportEl = document.getElementById('modal-transport-name');
      const countEl = document.getElementById('modal-node-count');

      if (nodeNameEl) {
        let name = 'Unbekannt';
        if (myInfo) {
          const known = this.fusionEngine.getKnownNode(myInfo.myNodeNum);
          name = known?.longName || known?.shortName || `Node !${myInfo.myNodeNum.toString(16)}`;
        }
        nodeNameEl.textContent = name;
      }
      if (nodeHexEl) {
        nodeHexEl.textContent = myInfo ? `!${myInfo.myNodeNum.toString(16)}` : '--';
      }
      if (transportEl) {
        const currentName = this.getTransportName();
        transportEl.textContent =
          currentName === 'ble'
            ? 'Bluetooth LE'
            : currentName === 'serial'
              ? 'USB Seriell'
              : currentName;
      }
      if (countEl) {
        countEl.textContent = `${count} Nodes`;
      }
    } else if (state === 'SYNCING_NODEDB') {
      indicator.classList.add('syncing');
      title.textContent = 'Synchronisiere...';
      const count = this.radioClient.getSyncedNodesCount();
      detail.textContent = `Lade NodeDB (${count} Nodes empfangen)`;
      connectOptions.style.display = 'none';
      connectedActions.style.display = 'flex';
      if (btnSync) btnSync.style.display = '';
      if (disconnectSpan) disconnectSpan.textContent = 'Trennen';
    } else if (state === 'CONNECTING') {
      indicator.classList.add('syncing');
      title.textContent = 'Verbindungsaufbau...';
      detail.textContent = 'Handshake mit Meshtastic-Gerät läuft';
      connectOptions.style.display = 'none';
      connectedActions.style.display = 'none';
    } else if (state === 'RECONNECTING') {
      indicator.classList.add('syncing');
      title.textContent = 'Automatische Wiederverbindung...';
      const attempts = this.radioClient.getReconnectAttempts();
      const maxAttempts = this.radioClient.getMaxReconnectAttempts();
      detail.textContent = `Verbindungsversuch ${attempts} von ${maxAttempts} (exponentieller Backoff)`;
      connectOptions.style.display = 'none';
      connectedActions.style.display = 'flex';
      if (btnSync) btnSync.style.display = 'none';
      if (disconnectSpan) disconnectSpan.textContent = 'Reconnect abbrechen';
    } else if (state === 'ERROR') {
      indicator.classList.add('error');
      title.textContent = 'Verbindungsfehler';
      detail.textContent = 'Verbindung fehlgeschlagen. Bitte erneut verbinden.';
      connectOptions.style.display = 'flex';
      connectedActions.style.display = 'none';
    } else {
      title.textContent = 'Nicht verbunden';
      detail.textContent = 'Wähle eine Verbindungsmethode für Dein Meshtastic-Funkgerät';
      connectOptions.style.display = 'flex';
      connectedActions.style.display = 'none';
    }
  }
}
